import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClients, useVentes } from "@/lib/donnees";
import { autorise, useNiveau } from "@/lib/session";
import { supabase } from "@/lib/supabase";
import { Kpi, Kpis, Section, Tag } from "@/ui/primitives";
import { Champ, Drawer } from "@/ui/Drawer";
import { Icon } from "@/ui/Icon";
import { Table, dansUnChamp } from "@/ui/Table";
import { useToast } from "@/ui/Toast";
import { entier, euroCourt } from "@/lib/format";
import type { Client, TypeClient } from "@/types/metier";

const VIDE = {
  nom: "", type: "lead" as TypeClient, segment: "", ville: "", pays: "", tva: "",
};

export function Clients() {
  const navigue = useNavigate();
  const toast = useToast();
  const peutCreer = autorise(useNiveau(), "agent");
  const { donnees: clients, chargement, erreur, recharger } = useClients();
  const { donnees: ventes } = useVentes();
  const [saisie, setSaisie] = useState<typeof VIDE | null>(null);
  const [occupe, setOccupe] = useState(false);

  const ca = useMemo(() => {
    const m = new Map<string, number>();
    for (const v of ventes) {
      if (!v.client_id) continue;
      m.set(v.client_id, (m.get(v.client_id) ?? 0) + Number(v.montant));
    }
    return m;
  }, [ventes]);

  const actifs = clients.filter((c) => c.type === "client").length;

  async function creer() {
    if (!saisie || occupe) return;
    const nom = saisie.nom.trim();
    if (!nom) {
      toast("Le nom de la société est obligatoire.");
      return;
    }
    setOccupe(true);
    const { data, error } = await supabase
      .from("clients")
      .insert({
        nom,
        type: saisie.type,
        segment: saisie.segment.trim() || null,
        ville: saisie.ville.trim() || null,
        pays: saisie.pays.trim() || null,
        tva: saisie.tva.trim() || null,
      })
      .select("id")
      .single();
    setOccupe(false);
    if (error) {
      toast(error.message);
      return;
    }
    setSaisie(null);
    recharger();
    toast(`${nom} ajouté.`);
    navigue(`/clients/${(data as { id: string }).id}`);
  }

  if (erreur) return <div className="pad"><div className="banner">{erreur}</div></div>;

  return (
    <div className="pad">
      <Kpis>
        <Kpi k="Clients" v={entier(actifs)} n="comptes ouverts" />
        <Kpi k="Prospects" v={entier(clients.length - actifs)} n="à convertir" />
        <Kpi
          k="Volume porté"
          v={euroCourt([...ca.values()].reduce((s, v) => s + v, 0))}
          n="toutes ventes confondues"
        />
      </Kpis>

      <Section
        titre="Clients et prospects"
        note={chargement ? "chargement…" : `${clients.length} fiches`}
        droite={
          peutCreer ? (
            <button type="button" className="btn btn-sm" onClick={() => setSaisie({ ...VIDE })}>
              <Icon n="plus" cls="" />
              Nouveau
            </button>
          ) : null
        }
      >
        <Table
          lignes={clients}
          cle={(c: Client) => c.id}
          chercher="Société ou ville"
          onClic={(c, ev) => {
            if (!dansUnChamp(ev)) navigue(`/clients/${c.id}`);
          }}
          colonnes={[
            { cle: "nom", titre: "Société", filtre: "texte",
              sous: (c) => [c.ville, c.pays].filter(Boolean).join(", ") || "—" },
            { cle: "type", titre: "Type", filtre: "select",
              texte: (c) => (c.type === "client" ? "Client" : "Prospect"),
              rendu: (c) =>
                c.type === "client" ? <Tag ton="ok" dot>client</Tag> : <Tag dot>prospect</Tag> },
            { cle: "segment", titre: "Segment", filtre: "select", mobile: false },
            { cle: "ville", titre: "Ville", mobile: false },
            { cle: "pays", titre: "Pays", filtre: "select", mobile: false },
            { cle: "tva", titre: "N° TVA", type: "ref", mobile: false },
            { cle: "ca", titre: "Volume", type: "euro", total: true,
              nombre: (c) => ca.get(c.id) ?? 0 },
          ]}
        />
      </Section>

      {saisie ? (
        <Drawer
          titre="Nouveau client"
          sous="Un prospect devient client à sa première vente réglée."
          onClose={() => setSaisie(null)}
          pied={
            <button
              type="button"
              className="btn btn-primary auth-entrer"
              disabled={occupe || !saisie.nom.trim()}
              onClick={() => void creer()}
            >
              {occupe ? "Enregistrement…" : "Créer la fiche"}
            </button>
          }
        >
          <Champ id="n-nom" label="Société">
            <input
              id="n-nom"
              type="text"
              autoFocus
              value={saisie.nom}
              onChange={(e) => setSaisie({ ...saisie, nom: e.target.value })}
            />
          </Champ>
          <Champ id="n-type" label="Type">
            <select
              id="n-type"
              value={saisie.type}
              onChange={(e) => setSaisie({ ...saisie, type: e.target.value as TypeClient })}
            >
              <option value="lead">Prospect</option>
              <option value="client">Client</option>
            </select>
          </Champ>
          <Champ id="n-segment" label="Segment">
            <input
              id="n-segment"
              type="text"
              placeholder="Grossiste, industrie, détail…"
              value={saisie.segment}
              onChange={(e) => setSaisie({ ...saisie, segment: e.target.value })}
            />
          </Champ>
          <div className="champ-duo">
            <Champ id="n-ville" label="Ville">
              <input
                id="n-ville"
                type="text"
                value={saisie.ville}
                onChange={(e) => setSaisie({ ...saisie, ville: e.target.value })}
              />
            </Champ>
            <Champ id="n-pays" label="Pays">
              <input
                id="n-pays"
                type="text"
                value={saisie.pays}
                onChange={(e) => setSaisie({ ...saisie, pays: e.target.value })}
              />
            </Champ>
          </div>
          <Champ id="n-tva" label="N° de TVA intracommunautaire">
            <input
              id="n-tva"
              type="text"
              placeholder="FR78908415789"
              value={saisie.tva}
              onChange={(e) => setSaisie({ ...saisie, tva: e.target.value })}
            />
          </Champ>
        </Drawer>
      ) : null}
    </div>
  );
}
